import { useForm } from "react-hook-form";
import { Button } from "../components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Box from "../components/ui/Box";
import Title from "../components/ui/Title";
import { Switch } from "../components/ui/switch";
import { useLoaderData } from "react-router-dom";
import { Civilite } from "../lib/interfaces/civilite.interface";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { User } from "../lib/interfaces/user.interface";
import { zodResolver } from "@hookform/resolvers/zod";
import { coordonneesFormSchema } from "../lib/schemas/coordonnees-form-schema";
import { z } from "zod";
import { toast } from "sonner";
import { useMutation, useQuery } from "@tanstack/react-query";
import axiosClient from "../lib/axiosClient";
import { TypePays } from "../lib/enums/type-pays.enum";
import { Check } from "lucide-react";
import ChangerMotDePasseDialog from "../components/dialogs/ChangerMotDePasseDialog";
import UpdateEmailDialog from "../components/dialogs/UpdateEmailDialog";
import { ksUserQK } from "../lib/query-keys";
import { findUser } from "../lib/queries/user-queries";

type LoaderData = {
  civilites: Civilite[];
  user: User;
};

type FieldValues = z.infer<typeof coordonneesFormSchema>;

const CoordonneesPage = () => {
  const [parent] = useAutoAnimate();
  const { civilites, user: prefetchedUser } = useLoaderData() as LoaderData;
  const { data: user } = useQuery({
    queryKey: [ksUserQK],
    queryFn: findUser,
    initialData: prefetchedUser,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const form = useForm<FieldValues>({
    resolver: zodResolver(coordonneesFormSchema),
    defaultValues: {
      siret: user.siret || "",
      noTva: user.noTva || "",
      adresse1: user.adresse1 || "",
      adresse1Livraison: user.adresse1Livraison || "",
      adresse2: user.adresse2 || "",
      adresse2Livraison: user.adresse2Livraison || "",
      civiliteId: user.civiliteId || "",
      codePostal: user.codePostal || "",
      codePostalLivraison: user.codePostalLivraison || "",
      dateDeNaissance: !!user.dateDeNaissance ? user.dateDeNaissance : undefined,
      envoyerAdresseDifferente: user.envoyerAdresseDifferente || false,
      nom: user.nom || "",
      nomLivraison: user.nomLivraison || "",
      entrepriseLivraison: user.entrepriseLivraison || "",
      typePays: user.typePays || TypePays.FRANCE,
      pays: user.pays || "FRANCE",
      paysLivraison: user.paysLivraison || "FRANCE",
      personneMorale: user.personneMorale || false,
      prenom: user.prenom || "",
      raisonSociale: user.raisonSociale || "",
      telephone: user.telephone || "",
      typePaysLivraison: user.typePaysLivraison || null,
      ville: user.ville || "",
      villeLivraison: user.villeLivraison || "",
    },
  });

  const { mutate: update, isPending } = useMutation({
    mutationFn: async (data: FieldValues) => {
      const coords = {
        siret: data.siret.trim(),
        noTva: data.noTva.trim(),
        nom: data.nom.trim().toUpperCase(),
        prenom: data.prenom.trim().toUpperCase(),
        civiliteId: data.personneMorale ? null : data.civiliteId,
        raisonSociale: data.personneMorale ? data.raisonSociale.trim().toUpperCase() : "",
        dateDeNaissance: data.personneMorale ? "" : data.dateDeNaissance,
        adresse1: data.adresse1.trim().toUpperCase(),
        adresse2: data.adresse2.trim().toUpperCase() || "",
        codePostal: data.codePostal.trim(),
        ville: data.ville.trim().toUpperCase(),
        pays:
          data.typePays === TypePays.F || data.typePays === TypePays.FRANCE ? "FRANCE" : data.pays.trim().toUpperCase(),
        telephone: data.telephone.trim(),
        typePays: data.typePays,
        personneMorale: data.personneMorale,
        // Adresse de livraison
        envoyerAdresseDifferente: data.envoyerAdresseDifferente,
        nomLivraison: data.envoyerAdresseDifferente ? data.nomLivraison.trim().toUpperCase() : "",
        entrepriseLivraison: data.envoyerAdresseDifferente ? data.entrepriseLivraison.trim().toUpperCase() : "",
        adresse1Livraison: data.envoyerAdresseDifferente ? data.adresse1Livraison.trim().toUpperCase() : "",
        adresse2Livraison: data.envoyerAdresseDifferente ? data.adresse2Livraison.trim().toUpperCase() || "" : "",
        codePostalLivraison: data.envoyerAdresseDifferente ? data.codePostalLivraison.trim() : "",
        villeLivraison: data.envoyerAdresseDifferente ? data.villeLivraison.trim().toUpperCase() || "" : "",
        paysLivraison: data.envoyerAdresseDifferente
          ? data.typePaysLivraison === TypePays.F || data.typePaysLivraison === TypePays.FRANCE
            ? "FRANCE"
            : data.paysLivraison.trim().toUpperCase()
          : "FRANCE",
        typePaysLivraison: data.envoyerAdresseDifferente
          ? data.typePaysLivraison === TypePays.F
            ? TypePays.FRANCE
            : data.typePaysLivraison
          : TypePays.FRANCE,
      };
      // 1. Sauvegarder en ligne
      await axiosClient.patch(`/users/${user.id}/coordonnees`, coords);
    },
    onSuccess: () => {
      toast.success("Coordonnées modifiées avec succès");
    },
    onError: (err: any) => {
      console.error(err.response?.data || err);
      toast.error(`Une erreur s'est produite`);
    },
  });

  return (
    <Form {...form}>
      <Box className='mb-6'>
        <Title order={2}>Identifiants de connexion</Title>
        <div className='mt-5 font-semibold'>Adresse e-mail</div>
        <div className='text-sm'>{user.email}</div>
        <div className='flex flex-col md:flex-row gap-2 md:gap-6 mt-2'>
          <UpdateEmailDialog />
          <ChangerMotDePasseDialog />
        </div>
      </Box>

      <form onSubmit={form.handleSubmit((data) => update(data))}>
        <Box className='grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-4'>
          <div className='lg:col-span-12 mb-2 mt-4'>
            <Title order={2}>Coordonnées de facturation</Title>
          </div>

          {/* Société ? */}
          <div className='lg:col-span-12'>
            <FormField
              control={form.control}
              name='personneMorale'
              render={({ field }) => (
                <FormItem className='flex flex-row items-center gap-4'>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className='space-y-0.5'>
                    <FormLabel className='text-base'>Ce compte est une société</FormLabel>
                  </div>
                </FormItem>
              )}
            />
          </div>

          {/* Civilité */}
          {!form.watch("personneMorale") && (
            <div className='lg:col-span-2'>
              <FormField
                control={form.control}
                name='civiliteId'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Civilité</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder='Sélectionner' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {civilites.map((civ) => (
                          <SelectItem key={civ.id} value={civ.id}>
                            {civ.nom}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
          {/* Raison sociale */}
          {form.watch("personneMorale") && (
            <div className='lg:col-span-4'>
              <FormField
                control={form.control}
                name='raisonSociale'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Raison sociale</FormLabel>
                    <FormControl>
                      <Input required {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
          {/* Nom */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='nom'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Prénom */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='prenom'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prénom</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Date de naissance */}
          {!form.watch("personneMorale") && (
            <div className='lg:col-span-2'>
              <FormField
                control={form.control}
                name='dateDeNaissance'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date de naissance</FormLabel>
                    <FormControl>
                      <Input type='date' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
          {/* Telephone */}
          <div className='lg:col-span-2'>
            <FormField
              control={form.control}
              name='telephone'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Téléphone</FormLabel>
                  <FormControl>
                    <Input placeholder='En cas de problème' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Adresse 1 */}
          <div className='lg:col-span-4'>
            <FormField
              control={form.control}
              name='adresse1'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>N° et rue ou lieu dit</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Adresse 2 */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='adresse2'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Complément d'adresse</FormLabel>
                  <FormControl>
                    <Input placeholder='Appartement, Bâtiment, Résidence, BP' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Code postal */}
          <div className='lg:col-span-2'>
            <FormField
              control={form.control}
              name='codePostal'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code postal</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Ville */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='ville'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ville</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Type de pays */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='typePays'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pays ou territoire</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder='Sélectionner' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={TypePays.FRANCE}>France</SelectItem>
                      <SelectItem value={TypePays.DROM_COM}>DROM-COM sauf Guyane ou Mayotte</SelectItem>
                      <SelectItem value={TypePays.MAYOTTE_GUYANNE}>Mayotte ou Guyane</SelectItem>
                      <SelectItem value={TypePays.UE}>Union Européenne</SelectItem>
                      <SelectItem value={TypePays.HORS_UE}>Hors Union Européenne</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Préciser le pays */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='pays'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Préciser le pays ou territoire</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={form.watch("typePays") === "F"} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* SIRET */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='siret'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Votre n° SIRET</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* SIRET */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='noTva'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>N° de TVA</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Box>
        {/* Envoyer à une adresse différente ? */}
        <FormField
          control={form.control}
          name='envoyerAdresseDifferente'
          render={({ field }) => (
            <FormItem className='flex flex-row items-center gap-4 mb-4 mt-5'>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <div className='space-y-0.5'>
                <FormLabel className='text-base'>Je souhaite recevoir la revue à une adresse différente</FormLabel>
              </div>
            </FormItem>
          )}
        />

        {/* Formulaire d'adresse de livraison */}
        <div ref={parent}>
          {form.watch("envoyerAdresseDifferente") && (
            <Box className='grid grid-cols-1 lg:grid-cols-12 md:gap-4'>
              <div className='lg:col-span-12'>
                <Title order={3}>Adresse de livraison</Title>
              </div>
              {/* Nom livraison */}
              <div className='lg:col-span-6'>
                <FormField
                  control={form.control}
                  name='nomLivraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom et prénom</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className='lg:col-span-6'>
                <FormField
                  control={form.control}
                  name='entrepriseLivraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Entreprise</FormLabel>
                      <FormControl>
                        <Input placeholder='Facultatif' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Adresse 1 livraison */}
              <div className='lg:col-span-6'>
                <FormField
                  control={form.control}
                  name='adresse1Livraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>N° et rue ou lieu dit</FormLabel>
                      <FormControl>
                        <Input required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Adresse 2 livraison */}
              <div className='lg:col-span-6'>
                <FormField
                  control={form.control}
                  name='adresse2Livraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Appartement, Bâtiment, Résidence, BP</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Code postal livraison */}
              <div className='lg:col-span-2'>
                <FormField
                  control={form.control}
                  name='codePostalLivraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code postal</FormLabel>
                      <FormControl>
                        <Input required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Ville livraison */}
              <div className='lg:col-span-3'>
                <FormField
                  control={form.control}
                  name='villeLivraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ville</FormLabel>
                      <FormControl>
                        <Input required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Type de pays livraison */}
              <div className='lg:col-span-3'>
                <FormField
                  control={form.control}
                  name='typePaysLivraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pays ou territoire</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Sélectionner' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={TypePays.FRANCE}>France</SelectItem>
                          <SelectItem value={TypePays.DROM_COM}>DROM-COM sauf Guyane ou Mayotte</SelectItem>
                          <SelectItem value={TypePays.MAYOTTE_GUYANNE}>Mayotte ou Guyane</SelectItem>
                          <SelectItem value={TypePays.UE}>Union Européenne</SelectItem>
                          <SelectItem value={TypePays.HORS_UE}>Hors Union Européenne</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Préciser le pays de livraison */}
              <div className='lg:col-span-4'>
                <FormField
                  control={form.control}
                  name='paysLivraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Préciser le pays ou territoire</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          required={form.watch("envoyerAdresseDifferente") && form.watch("typePaysLivraison") !== "F"}
                          disabled={form.watch("typePaysLivraison") === "F"}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Box>
          )}
        </div>

        {/* <div>
          {form.formState.errors && (
            <div className='text-red-500'>
              {Object.values(form.formState.errors).map((error) => (
                <div key={error.message}>{error.message}</div>
              ))}
            </div>
          )}
        </div> */}

        <div className='text-right mb-8 mt-6 md:mt-5'>
          <Button icon={<Check />} type='submit' loading={isPending}>
            Sauvegarder
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CoordonneesPage;
